import { IANAZone } from 'luxon'

// TODO: this bidding market program should be removed in favor of using the MarketProgramType enum from src/types/marketProgramType.
export enum MarketProgram {
  AFRR_UP = 'AFRR_UP',
  AFRR_DOWN = 'AFRR_DOWN',
  FCRD_UP = 'FCRD_UP',
  FCRD_DOWN = 'FCRD_DOWN',
  FCRN = 'FCRN',
  FFR_UP = 'FFR_UP',
  MFRR_UP = 'MFRR_UP',
  MFRR_DOWN = 'MFRR_DOWN',
  NORDICS_FCRD_DOWN_STATIC = 'NORDICS_FCRD_DOWN_STATIC',
  NORDICS_FCRD_UP_STATIC = 'NORDICS_FCRD_UP_STATIC',
  NORDICS_FCRD_DOWN_DYNAMIC = 'NORDICS_FCRD_DOWN_DYNAMIC',
  NORDICS_FCRD_UP_DYNAMIC = 'NORDICS_FCRD_UP_DYNAMIC',
  NORDICS_FCRN = 'NORDICS_FCRN',
  SYMBALANCE_UP = 'SYMBALANCE_UP',
  SYMBALANCE_DOWN = 'SYMBALANCE_DOWN',
  WHOLESALE_BUY = 'WHOLESALE_BUY',
  WHOLESALE_SELL = 'WHOLESALE_SELL',
}

export enum ManualAcceptFlow {
  CREATE = 'create',
  EDIT = 'edit',
}

export enum Status {
  TODO = 'TODO',
  DRAFT = 'DRAFT',
  OFFERED = 'OFFERED',
  PARTIALLY_ACCEPTED = 'PARTIALLY_ACCEPTED',
  BUYBACK = 'BUYBACK',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  OVERBID_ACCEPTED = 'OVERBID_ACCEPTED',
  MANUALLY_ACCEPTED = 'MANUALLY_ACCEPTED',
  MANUALLY_EDITED = 'MANUALLY_EDITED',
  NO_BID = 'NO_BID',
}

export enum BidType {
  CAPACITY = 'CAPACITY',
  ENERGY = 'ENERGY',
}

// Status of a bid update. Distinct from Status: it tracks whether the update has been
// acknowledged by the receiving party rather than the acceptance outcome of offered volumes.
export enum BidUpdateStatus {
  PENDING = 'PENDING',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  REJECTED = 'REJECTED',
  FAILED = 'FAILED',
}

// Who or what created a bid update: a human trader, an automated bot, or unknown/legacy data.
export enum BidUpdateCreatedBy {
  TRADER = 'TRADER',
  BOT = 'BOT',
  UNKNOWN = 'UNKNOWN',
}

export type MarketClosingTime = {
  hour: number
  minute: number
  second: number
}

export const MARKET_TIMEZONE = new IANAZone('Europe/Paris')

export const MAX_PTU_ROWS_PER_DATAGRID = 100 // on daylight savings time we can have up to 96+4 15-minute PTUs per day

export const DEFAULT_MARKET_CLOSING_TIME: MarketClosingTime = { hour: 17, minute: 0, second: 0 }

export const MFRR_MARKET_CLOSING_TIME: MarketClosingTime = { hour: 7, minute: 30, second: 0 }

export const MARKET_CLOSING_WARNING_MINUTES_BEFORE = 60

export const MAXIMUM_MW_PER_PTU = 1000

export const OLD_MARKET_PROGRAMS = [MarketProgram.FCRD_DOWN, MarketProgram.FCRD_UP]

// Market programs whose resource selection is handled by the monolith (sympower-server), which does not read
// portfolio-manager activation schedules, so the per-resource activatable flag has no effect for them.
export const MONOLITH_MARKET_PROGRAMS = [
  MarketProgram.FCRD_UP,
  MarketProgram.FCRD_DOWN,
  MarketProgram.FCRN,
  MarketProgram.FFR_UP,
]

export const MFRR_MARKET_PROGRAMS = [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN]
export const AFRR_MARKET_PROGRAMS = [MarketProgram.AFRR_UP, MarketProgram.AFRR_DOWN]

export const NORDICS_FCRD_STATIC_MARKET_PROGRAMS = [
  MarketProgram.NORDICS_FCRD_UP_STATIC,
  MarketProgram.NORDICS_FCRD_DOWN_STATIC,
]

export const SYMBALANCE_MARKET_PROGRAMS = [MarketProgram.SYMBALANCE_UP, MarketProgram.SYMBALANCE_DOWN]

export const FCRD_MARKET_PROGRAMS = [MarketProgram.NORDICS_FCRD_UP_DYNAMIC, MarketProgram.NORDICS_FCRD_DOWN_DYNAMIC]

export const ALL_RESULTS = 10_000

export const DEFAULT_CURRENCY = 'EUR'

export const RELEVANT_CAPACITY_STATUSES_TO_DISPLAY_ACCEPTED_VOLUME = [
  Status.ACCEPTED,
  Status.MANUALLY_ACCEPTED,
  Status.MANUALLY_EDITED,
  Status.PARTIALLY_ACCEPTED,
  Status.REJECTED,
  Status.OVERBID_ACCEPTED,
]

export const CAPACITY_MARKET_RESULT_STATUSES = [
  Status.ACCEPTED,
  Status.PARTIALLY_ACCEPTED,
  Status.REJECTED,
  Status.MANUALLY_ACCEPTED,
  Status.MANUALLY_EDITED,
  Status.BUYBACK,
  Status.OVERBID_ACCEPTED,
]

export const SUBMITTED_BID_STATUSES = [
  Status.ACCEPTED,
  Status.MANUALLY_ACCEPTED,
  Status.MANUALLY_EDITED,
  Status.PARTIALLY_ACCEPTED,
  Status.REJECTED,
  Status.OFFERED,
  Status.OVERBID_ACCEPTED,
  Status.BUYBACK,
]

export const ENTELIOS_ALLOWED_PRICE_STEPS_PER_PTU = 1
export const ENTELIOS_PORTFOLIO_IDENTIFIER = 'ENTELIOS'

/**
 * Cascade market configurations for sequential bidding flow.
 * Each market has a gate closure hour and maps to underlying MarketProgram values.
 */
export const CASCADE_MARKETS = [
  {
    id: 'mfrr' as const,
    label: 'mFRR',
    gateHour: 8,
    gateDisplay: '08:00',
    marketPrograms: [MarketProgram.MFRR_UP, MarketProgram.MFRR_DOWN],
  },
  {
    id: 'da-wholesale' as const,
    label: 'Day Ahead',
    gateHour: 12,
    gateDisplay: '12:00',
    marketPrograms: [MarketProgram.WHOLESALE_BUY, MarketProgram.WHOLESALE_SELL],
  },
  {
    id: 'fcr-d' as const,
    label: 'FCR-D',
    gateHour: 17,
    gateDisplay: '17:00',
    marketPrograms: [MarketProgram.NORDICS_FCRD_UP_DYNAMIC, MarketProgram.NORDICS_FCRD_DOWN_DYNAMIC],
  },
  {
    id: 'fcr-n' as const,
    label: 'FCR-N',
    gateHour: 17,
    gateDisplay: '17:00',
    marketPrograms: [MarketProgram.NORDICS_FCRN],
  },
] as const
